/**
 * Upgrade Paths Tab
 * V6.15 V2: Subscription flow analysis with opportunity matrix
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Skeleton, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import apiService from '../../../services/api';

interface FlowItem {
  fromTier: string;
  toTier: string;
  changeType: string;
  count: number;
}

interface OpportunityItem {
  tier: string;
  displayName: string;
  subscriberCount: number;
  monthlyPrice: number;
  annualPrice: number;
  tierOrder: number;
}

interface UpgradeData {
  hasHistory: boolean;
  flows: FlowItem[];
  summary: {
    upgrades: number;
    downgrades: number;
    cancellations: number;
    reactivations: number;
    newSubs: number;
  };
  opportunity: OpportunityItem[];
}

const TIER_COLORS: Record<string, string> = {
  standard: '#0ea5e9',
  premium: '#7c3aed',
  legacy_creator: '#d97706',
  vault_standard: '#146152',
  vault_premium: '#1A5F7A',
  none: '#9CA3AF',
  other: '#6B7280',
};

const KpiCard: React.FC<{ label: string; value: string | number; color?: string; sub?: string }> = ({ label, value, color, sub }) => (
  <Paper sx={{ p: 2, borderRadius: '12px', border: '1px solid #e5e7eb', textAlign: 'center' }}>
    <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '10px', fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
      {label}
    </Typography>
    <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '24px', fontWeight: 800, color: color || '#111827', mt: 0.5 }}>
      {value}
    </Typography>
    {sub && (
      <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '10px', color: '#9CA3AF', mt: 0.25 }}>
        {sub}
      </Typography>
    )}
  </Paper>
);

const tierLabel = (t: string) => t.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

interface UpgradePathsTabProps {
  timeRangeDays?: string;
}

const UpgradePathsTab: React.FC<UpgradePathsTabProps> = ({ timeRangeDays }) => {
  const [data, setData] = useState<UpgradeData | null>(null);
  const [loading, setLoading] = useState(true);
  const days = timeRangeDays ?? '90';

  useEffect(() => {
    setLoading(true);
    const load = async () => {
      try {
        const response = await apiService.get(`/api/admin/subscriptions/intelligence/upgrade-paths?days=${days}`);
        if (response.data) {
          setData(response.data as UpgradeData);
        }
      } catch (e) {
        console.error('Failed to load upgrade paths:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [days]);

  if (loading) {
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 2 }}>
          {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} variant="rectangular" height={80} sx={{ borderRadius: 2 }} />)}
        </Box>
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '14px', color: '#6B7280' }}>
          Unable to load upgrade paths data
        </Typography>
      </Box>
    );
  }

  const { summary } = data;

  // Build opportunity matrix with next-tier info
  const sortedTiers = [...data.opportunity].sort((a, b) => a.tierOrder - b.tierOrder);
  const opportunityRows = sortedTiers.map((tier, idx) => {
    const nextTier = idx < sortedTiers.length - 1 ? sortedTiers[idx + 1] : null;
    const currentPrice = tier.monthlyPrice || (tier.annualPrice / 12);
    const nextPrice = nextTier ? (nextTier.monthlyPrice || (nextTier.annualPrice / 12)) : 0;
    const mrrImpact = nextTier ? parseFloat(((nextPrice - currentPrice) * tier.subscriberCount * 0.1).toFixed(2)) : 0;

    return {
      ...tier,
      nextTierName: nextTier ? nextTier.displayName : null,
      estimatedMrrImpact: mrrImpact,
    };
  });

  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      {/* Summary KPIs */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 2 }}>
        <KpiCard label="Upgrades" value={summary.upgrades} color="#10B981" sub={`Last ${days} days`} />
        <KpiCard label="Downgrades" value={summary.downgrades} color="#F59E0B" sub={`Last ${days} days`} />
        <KpiCard label="Cancellations" value={summary.cancellations} color="#EF4444" sub={`Last ${days} days`} />
        <KpiCard label="Reactivations" value={summary.reactivations} color="#0ea5e9" sub={`Last ${days} days`} />
        <KpiCard label="New Subs" value={summary.newSubs} color="#1A5F7A" sub={`Last ${days} days`} />
      </Box>

      {/* Flow Visualization */}
      <Paper sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e5e7eb' }}>
        <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', fontWeight: 700, color: '#111827', mb: 1.5 }}>
          Plan Change Flows
        </Typography>
        {!data.hasHistory || data.flows.length === 0 ? (
          <Alert
            severity="info"
            sx={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: '12px',
              borderRadius: '10px',
              bgcolor: '#EFF6FF',
              '& .MuiAlert-icon': { color: '#1A5F7A' },
            }}
          >
            {!data.hasHistory
              ? 'Plan change tracking has been activated. Flow data will appear as members change their subscription plans.'
              : `No plan changes detected in the last ${days} days. Subscribers are staying on their current plans.`}
          </Alert>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
            {data.flows.slice(0, 12).map((flow, i) => {
              const isUpgrade = flow.changeType === 'upgrade';
              const isDowngrade = flow.changeType === 'downgrade';
              const color = isUpgrade ? '#10B981' : isDowngrade ? '#EF4444' : '#6B7280';
              const arrow = isUpgrade ? '\u2191' : isDowngrade ? '\u2193' : '\u2192';

              return (
                <Paper
                  key={i}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: '10px',
                    borderColor: color,
                    borderWidth: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: TIER_COLORS[flow.fromTier] || '#6B7280' }} />
                      <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '10px', color: '#6B7280' }}>
                        {tierLabel(flow.fromTier)}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '16px', color, fontWeight: 800, textAlign: 'center', my: 0.25 }}>
                      {arrow}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: TIER_COLORS[flow.toTier] || '#6B7280' }} />
                      <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '10px', color: '#6B7280' }}>
                        {tierLabel(flow.toTier)}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '18px', fontWeight: 800, color }}>
                      {flow.count}
                    </Typography>
                    <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '9px', color: '#9CA3AF', textTransform: 'uppercase' }}>
                      {flow.changeType}
                    </Typography>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        )}
      </Paper>

      {/* Opportunity Matrix */}
      <Paper sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e5e7eb' }}>
        <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', fontWeight: 700, color: '#111827', mb: 1.5 }}>
          Upgrade Opportunity Matrix
        </Typography>
        <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11px', color: '#6B7280', mb: 1.5 }}>
          Estimated MRR impact assumes 10% conversion to next tier
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Current Tier', 'Subscribers', 'Current Price', 'Next Tier', 'MRR Impact (10%)', ''].map(h => (
                  <TableCell key={h} sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '10px', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', borderBottom: '2px solid #E5E7EB' }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {opportunityRows.map(row => (
                <TableRow key={row.tier} hover>
                  <TableCell sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '12px', fontWeight: 600 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: TIER_COLORS[row.tier] || '#6B7280' }} />
                      {row.displayName}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '12px', fontWeight: 700 }}>
                    {row.subscriberCount}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '12px', color: '#6B7280' }}>
                    ${row.monthlyPrice > 0 ? `${row.monthlyPrice}/mo` : row.annualPrice > 0 ? `${(row.annualPrice / 12).toFixed(2)}/mo` : '\u2014'}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '12px', color: '#6B7280' }}>
                    {row.nextTierName || '\u2014 (Top tier)'}
                  </TableCell>
                  <TableCell sx={{
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: '12px',
                    fontWeight: 700,
                    color: row.estimatedMrrImpact > 0 ? '#10B981' : '#6B7280',
                  }}>
                    {row.estimatedMrrImpact > 0 ? `+$${row.estimatedMrrImpact.toFixed(2)}` : '\u2014'}
                  </TableCell>
                  <TableCell>
                    {row.estimatedMrrImpact > 0 && (
                      <Box
                        sx={{
                          width: Math.min(row.estimatedMrrImpact * 2, 100),
                          height: 6,
                          bgcolor: '#10B981',
                          borderRadius: 3,
                          opacity: 0.6,
                        }}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default UpgradePathsTab;
